import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Bell, BellRing } from 'lucide-react';
import { NotificationService } from '@/services/shared/notificationService';
import { useAuthUnified } from '@/hooks/shared/useAuthUnified';

interface NotificationBellProps {
  unreadCount?: number;
  onToggle: (isOpen: boolean) => void;
  isOpen?: boolean;
  className?: string;
}

/**
 * NotificationBell component - Enhanced bell icon with animations and better UX
 *
 * Features:
 * - Smooth animations for badge updates
 * - Visual feedback for interactions
 * - Accessibility improvements
 * - Performance optimizations
 * - Enhanced hover states and tooltips
 */
export const NotificationBell: React.FC<NotificationBellProps> = ({
  unreadCount: propUnreadCount,
  onToggle,
  isOpen = false,
  className = ''
}) => {
  const { effectiveUser } = useAuthUnified();
  const [unreadCount, setUnreadCount] = useState(propUnreadCount || 0);
  const [previousCount, setPreviousCount] = useState(propUnreadCount || 0);
  const [hover, setHover] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  // Subscribe to real-time unread count updates with animation handling
  useEffect(() => {
    if (!effectiveUser?.employee_id) return;
    
    const unsubscribe = NotificationService.subscribeToUnreadCount(
      effectiveUser.employee_id.toString(),
      (count) => {
        // Trigger animation if count increased
        if (count > unreadCount) {
          setIsAnimating(true);
          setTimeout(() => setIsAnimating(false), 600); // Animation duration
        }
        
        setPreviousCount(unreadCount);
        setUnreadCount(count);
      }
    );

    return () => {
      unsubscribe();
    };
  }, [effectiveUser?.employee_id, unreadCount]);

  // Use prop value if provided, otherwise use state
  const displayCount = propUnreadCount !== undefined ? propUnreadCount : unreadCount;

  // Memoized click handler for performance
  const handleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent click from bubbling to parent overlays
    onToggle(!isOpen);
  }, [isOpen, onToggle]);

  // Memoized hover handlers
  const handleMouseEnter = useCallback(() => setHover(true), []);
  const handleMouseLeave = useCallback(() => setHover(false), []);

  // Memoized accessibility label
  const ariaLabel = useMemo(() => {
    if (displayCount > 0) {
      return `Notifications (${displayCount} unread)`;
    }
    return 'Notifications';
  }, [displayCount]);

  // Determine which bell icon to show
  const BellIcon = displayCount > 0 && isAnimating ? BellRing : Bell;

  return (
    <div className={`relative group ${className}`}>
      <button
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={`
          relative p-2 rounded-md transition-all duration-200 ease-in-out
          focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2
          ${isOpen
            ? 'bg-purple-50 text-purple-600 shadow-md scale-105'
            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100 hover:scale-105'
          }
          ${isAnimating ? 'animate-pulse' : ''}
        `}
        aria-label={ariaLabel}
        aria-expanded={isOpen}
        aria-haspopup="true"
        type="button"
      >
        <BellIcon className={`h-5 w-5 transition-transform duration-200 ${isAnimating ? 'animate-bounce' : ''}`} />
        
        {/* Unread count badge with enhanced animations */}
        {displayCount > 0 && (
          <div className={`
            absolute -top-1 -right-1 bg-orange-500 text-white text-xs rounded-full
            min-w-[18px] h-[18px] flex items-center justify-center px-1
            transition-all duration-300 ease-out
            ${isAnimating ? 'animate-ping' : ''}
            ${displayCount !== previousCount ? 'scale-110' : 'scale-100'}
          `}>
            <span className="relative z-10">
              {displayCount > 99 ? '99+' : displayCount}
            </span>
            {/* Pulse effect for new notifications */}
            {isAnimating && (
              <div className="absolute inset-0 bg-orange-500 rounded-full animate-ping opacity-75"></div>
            )}
          </div>
        )}
      </button>

      {/* Enhanced tooltip with better positioning */}
      <div className={`
        absolute left-1/2 transform -translate-x-1/2 top-full mt-2
        transition-all duration-200 pointer-events-none z-50
        ${hover ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-1'}
      `}>
        <div className="bg-gray-900 text-white text-xs rounded-md px-3 py-2 whitespace-nowrap shadow-lg">
          {displayCount > 0
            ? `${displayCount} unread notification${displayCount === 1 ? '' : 's'}`
            : 'No new notifications'
          }
          {/* Enhanced arrow */}
          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-900"></div>
        </div>
      </div>
    </div>
  );
};

